import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as AlertPrimitive from '@radix-ui/react-alert-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { useT } from '@/i18n'

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close

const overlay = 'fixed inset-0 z-40 bg-black/40 backdrop-blur-[1px]'

interface ContentProps extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  title: React.ReactNode
  description?: React.ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  footer?: React.ReactNode
}

const sizes = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' }

export function DialogContent({ title, description, size = 'md', footer, className, children, ...props }: ContentProps) {
  const t = useT()
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={overlay} />
      <DialogPrimitive.Content
        className={cn(
          'fixed left-1/2 top-[8vh] z-50 flex max-h-[84vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col rounded-[12px] border border-border bg-surface shadow-pop',
          sizes[size],
          className,
        )}
        {...(description ? {} : { 'aria-describedby': undefined })}
        {...props}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <DialogPrimitive.Title className="text-base font-semibold text-ink-strong">{title}</DialogPrimitive.Title>
            {description && <DialogPrimitive.Description className="mt-0.5 text-[13px] text-muted">{description}</DialogPrimitive.Description>}
          </div>
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('common.close')}>
              <X />
            </Button>
          </DialogPrimitive.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">{footer}</div>}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

interface SheetProps extends Omit<React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>, 'title'> {
  title: React.ReactNode
  description?: React.ReactNode
  width?: string
  headerExtra?: React.ReactNode
  side?: 'right' | 'left'
}

/** Side drawer (task detail, notifications, appointment drawer). */
export function SheetContent({ title, description, width = 'min(560px, 100vw)', headerExtra, side = 'right', className, children, ...props }: SheetProps) {
  const t = useT()
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/25" />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-0 z-50 flex h-dvh flex-col border-border bg-surface shadow-pop',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          className,
        )}
        style={{ width }}
        {...(description ? {} : { 'aria-describedby': undefined })}
        {...props}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0 flex-1">
            <DialogPrimitive.Title className="text-base font-semibold text-ink-strong">{title}</DialogPrimitive.Title>
            {description && <DialogPrimitive.Description className="mt-0.5 text-[13px] text-muted">{description}</DialogPrimitive.Description>}
          </div>
          {headerExtra}
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('common.close')}>
              <X />
            </Button>
          </DialogPrimitive.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
}

interface ConfirmProps {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: React.ReactNode
  description?: React.ReactNode
  confirmLabel: string
  onConfirm: () => unknown
  destructive?: boolean
  actionId?: string
  loading?: boolean
  children?: React.ReactNode
}

/** Confirmation dialog: the title states the consequence and the button repeats the verb. */
export function ConfirmDialog({ open, onOpenChange, title, description, confirmLabel, onConfirm, destructive, actionId, loading, children }: ConfirmProps) {
  const t = useT()
  return (
    <AlertPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertPrimitive.Portal>
        <AlertPrimitive.Overlay className={overlay} />
        <AlertPrimitive.Content className="fixed left-1/2 top-[20vh] z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 rounded-[12px] border border-border bg-surface p-5 shadow-pop">
          <AlertPrimitive.Title className="text-base font-semibold text-ink-strong">{title}</AlertPrimitive.Title>
          {description ? (
            <AlertPrimitive.Description className="mt-2 text-[13px] leading-relaxed text-muted">{description}</AlertPrimitive.Description>
          ) : (
            <AlertPrimitive.Description className="sr-only">{String(title)}</AlertPrimitive.Description>
          )}
          {children}
          <div className="mt-5 flex justify-end gap-2">
            <AlertPrimitive.Cancel asChild>
              <Button variant="outline">{t('common.cancel')}</Button>
            </AlertPrimitive.Cancel>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              actionId={actionId}
              loading={loading}
              onClick={async () => {
                await onConfirm()
              }}
            >
              {confirmLabel}
            </Button>
          </div>
        </AlertPrimitive.Content>
      </AlertPrimitive.Portal>
    </AlertPrimitive.Root>
  )
}
